<template>
  <Dropdown
    :trigger="trigger"
    v-bind="$attrs"
    :class="['inline-flex items-center leading-normal', $attrs.disabled ? 'opacity-50' : '']"
  >
    <span>
      <slot>
        <span class="flex-1">
          {{
            (options.find((i) => i.value === selectedKeys[0])?.label ?? selectedKeys[0]) ||
            t('common.chooseText')
          }}
        </span>
      </slot>
      <BasicArrow v-if="showArrow" down :size="20" />
    </span>
    <template #overlay>
      <Menu :selectedKeys="selectedKeys" v-bind="menuProps">
        <Menu.Item
          v-for="item in options"
          :key="`${item.value}`"
          v-bind="pick(item, ['class', 'disabled'])"
          @click="handleClickMenu(item)"
        >
          <Popconfirm
            v-if="popconfirm && item.popConfirm"
            v-bind="omit(item.popConfirm, ['icon'])"
            :disabled="item.disabled"
          >
            <template v-if="item.popConfirm.icon" #icon>
              <MIcon :icon="item.popConfirm.icon" />
            </template>
            <div>
              <MIcon v-if="item.icon" class="mr-1" :icon="item.icon" />
              <span>{{ item.label }}</span>
            </div>
          </Popconfirm>
          <template v-else>
            <MIcon v-if="item.icon" class="mr-1" :icon="item.icon" />
            <span>{{ item.label }}</span>
          </template>
        </Menu.Item>
      </Menu>
    </template>
  </Dropdown>
</template>

<script lang="ts" setup>
  import { Dropdown, Menu, type MenuProps, Popconfirm } from 'ant-design-vue';
  import { computed } from 'vue';
  import { omit, pick } from '@mts-fe/utils';
  import { BasicArrow } from '@mts/components/Basic';
  import { MIcon } from '@mts/components/Icon';
  import { t } from '@mts/hooks/web/useI18n';
  import type { DropItem } from './typing';

  type TriggerType = 'contextmenu' | 'click' | 'hover';
  const props = withDefaults(
    defineProps<{
      popconfirm?: boolean;
      trigger?: TriggerType[];
      value?: string | string[];
      options: DropItem[];
      showArrow?: boolean;
      menuProps?: MenuProps;
    }>(),
    {
      trigger: () => ['click'],
      options: () => [],
    },
  );

  const emit = defineEmits(['change', 'update:value']);

  const selectedKeys = computed({
    get: () => (typeof props.value === 'string' ? [props.value] : props.value || []),
    set: (val) => emit('update:value', typeof props.value === 'string' && val ? val[0] : val),
  });

  function handleClickMenu(item: DropItem) {
    const val = `${item.value}`;
    if (props.menuProps?.multiple) {
      const keys = [...selectedKeys.value];
      const index = keys.indexOf(val);
      if (index > -1) {
        keys.splice(index, 1);
      } else {
        keys.push(val);
      }
      selectedKeys.value = keys;
    } else {
      selectedKeys.value = [val];
    }

    emit('change', item);
    item.onClick?.();
  }
</script>
